/**
 * Advanced Backups Page - Platform & Tenant Backup Management
 * 
 * Features:
 * - Global platform backups
 * - Tenant-specific backups
 * - Database backups
 * - Restore with confirmation
 * - Full audit trail
 */

import React, { useState, useEffect } from 'react';
import {
  Database, Plus, Download, Trash2, RefreshCw, HardDrive,
  Calendar, FileArchive, Building2, Server, RotateCcw,
  AlertTriangle, CheckCircle, Filter, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import { api } from '../../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Backup type configuration
const BACKUP_TYPES = {
  GLOBAL_PLATFORM: {
    label: 'Plateforme',
    description: 'Données SaaS globales',
    icon: Server,
    color: 'bg-violet-100 text-violet-700',
    iconColor: 'text-violet-600'
  },
  TENANT: {
    label: 'Tenant',
    description: 'Données d\'un riad',
    icon: Building2,
    color: 'bg-blue-100 text-blue-700',
    iconColor: 'text-blue-600'
  },
  DATABASE: {
    label: 'Base de données',
    description: 'Dump complet SQLite',
    icon: Database,
    color: 'bg-green-100 text-green-700',
    iconColor: 'text-green-600'
  }
};

const BackupsPage = () => {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState({});
  const [riads, setRiads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [riadFilter, setRiadFilter] = useState('all');
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirmation, setRestoreConfirmation] = useState('');
  
  // Create form
  const [createType, setCreateType] = useState('GLOBAL_PLATFORM');
  const [selectedRiadId, setSelectedRiadId] = useState('');

  useEffect(() => {
    fetchBackups();
    fetchRiads();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter && typeFilter !== 'all') params.append('type', typeFilter);
      if (riadFilter && riadFilter !== 'all') params.append('riad_id', riadFilter);
      
      const { data } = await api.get(`/admin/backups?${params}`);
      setBackups(data.backups || []);
      setStats(data.stats || {});
    } catch (error) {
      toast.error('Erreur lors du chargement des backups');
    } finally {
      setLoading(false);
    }
  };

  const fetchRiads = async () => {
    try {
      const { data } = await api.get('/admin/backups/riads');
      setRiads(data.riads || []);
    } catch (error) {
      console.error('Error fetching riads:', error);
    }
  };

  useEffect(() => {
    fetchBackups();
  }, [typeFilter, riadFilter]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      let endpoint;
      let payload = {};
      
      switch (createType) {
        case 'GLOBAL_PLATFORM':
          endpoint = '/admin/backups/platform';
          break;
        case 'TENANT':
          if (!selectedRiadId) {
            toast.error('Veuillez sélectionner un tenant');
            setCreating(false);
            return;
          }
          endpoint = '/admin/backups/tenant';
          payload = { riad_id: parseInt(selectedRiadId) };
          break;
        case 'DATABASE':
          endpoint = '/admin/backups/database';
          break;
        default:
          return;
      }
      
      const { data } = await api.post(endpoint, payload);
      toast.success(`Backup lancé: ${data.filename}`);
      setCreateDialogOpen(false);
      setSelectedRiadId('');
      
      // Refresh after delay (backup runs in background)
      setTimeout(fetchBackups, 3000);
      
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (backup) => {
    try {
      const response = await api.get(
        `/admin/backups/${backup.path}/${backup.filename}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Téléchargement démarré');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      await api.delete(`/admin/backups/${deleteTarget.path}/${deleteTarget.filename}`);
      toast.success('Backup supprimé');
      fetchBackups();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    
    if (restoreConfirmation !== 'RESTORE_CONFIRM') {
      toast.error('Code de confirmation incorrect');
      return;
    }
    
    try {
      const { data } = await api.post(
        `/admin/backups/restore/tenant/${restoreTarget.filename}`,
        { confirmation_code: 'RESTORE_CONFIRM', mode: 'MERGE' }
      );
      toast.success(data.message);
      setRestoreTarget(null);
      setRestoreConfirmation('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la restauration');
    }
  };

  // Filter backups
  const filteredBackups = backups;

  // Render type badge
  const renderTypeBadge = (type) => {
    const config = BACKUP_TYPES[type] || BACKUP_TYPES.GLOBAL_PLATFORM;
    return (
      <Badge className={`${config.color} gap-1`}>
        <config.icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="backups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Backups</h1>
          <p className="text-muted-foreground">Sauvegardes plateforme et tenants</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchBackups} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-backup-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Backup
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center">
              <FileArchive className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total || 0}</p>
              <p className="text-sm text-muted-foreground">Total backups</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setTypeFilter('GLOBAL_PLATFORM')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center">
              <Server className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.by_type?.GLOBAL_PLATFORM || 0}</p>
              <p className="text-sm text-muted-foreground">Plateforme</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setTypeFilter('TENANT')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.by_type?.TENANT || 0}</p>
              <p className="text-sm text-muted-foreground">Tenants</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <HardDrive className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total_size_mb || 0} MB</p>
              <p className="text-sm text-muted-foreground">Espace utilisé</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-[200px]">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="GLOBAL_PLATFORM">Plateforme</SelectItem>
                  <SelectItem value="TENANT">Tenant</SelectItem>
                  <SelectItem value="DATABASE">Base de données</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[250px]">
              <Label className="text-xs text-muted-foreground">Tenant</Label>
              <Select value={riadFilter} onValueChange={setRiadFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les tenants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les tenants</SelectItem>
                  {riads.map(r => (
                    <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(typeFilter !== 'all' || riadFilter !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setTypeFilter('all'); setRiadFilter('all'); }}
              >
                Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Backup List */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des Backups</CardTitle>
          <CardDescription>Liste de tous les backups disponibles</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredBackups.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun backup disponible</p>
              <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                Créer le premier backup
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fichier</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Taille</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBackups.map(backup => (
                  <TableRow key={backup.id} data-testid={`backup-row-${backup.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          BACKUP_TYPES[backup.type]?.color.split(' ')[0] || 'bg-gray-100'
                        }`}>
                          {React.createElement(BACKUP_TYPES[backup.type]?.icon || FileArchive, {
                            className: `w-5 h-5 ${BACKUP_TYPES[backup.type]?.iconColor || 'text-gray-600'}`
                          })}
                        </div>
                        <div>
                          <p className="font-medium text-sm truncate max-w-[250px]">{backup.filename}</p>
                          <p className="text-xs text-muted-foreground">{backup.status}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{renderTypeBadge(backup.type)}</TableCell>
                    <TableCell>
                      {backup.riad_name ? (
                        <span className="text-sm">{backup.riad_name}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(backup.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{backup.size_mb} MB</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleDownload(backup)}>
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger
                          </DropdownMenuItem>
                          
                          {backup.type === 'TENANT' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => setRestoreTarget(backup)}
                                className="text-amber-600"
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Restaurer
                              </DropdownMenuItem>
                            </>
                          )}
                          
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteTarget(backup)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Backup Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un Backup</DialogTitle>
            <DialogDescription>
              Sélectionnez le type de sauvegarde à effectuer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Type de backup</Label>
              <Select value={createType} onValueChange={setCreateType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GLOBAL_PLATFORM">
                    <div className="flex items-center gap-2">
                      <Server className="w-4 h-4 text-violet-600" />
                      Plateforme SaaS
                    </div>
                  </SelectItem>
                  <SelectItem value="TENANT">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      Tenant spécifique
                    </div>
                  </SelectItem>
                  <SelectItem value="DATABASE">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-green-600" />
                      Base de données
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {createType === 'TENANT' && (
              <div>
                <Label>Tenant à sauvegarder</Label>
                <Select value={selectedRiadId} onValueChange={setSelectedRiadId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {riads.map(r => (
                      <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Info box */}
            <div className={`p-4 rounded-lg ${
              createType === 'GLOBAL_PLATFORM' ? 'bg-violet-50 border-violet-200' :
              createType === 'TENANT' ? 'bg-blue-50 border-blue-200' :
              'bg-green-50 border-green-200'
            } border`}>
              <p className="text-sm">
                {createType === 'GLOBAL_PLATFORM' && (
                  <>
                    <strong>Backup Plateforme:</strong> Sauvegarde les comptes SaaS, plans, abonnements, 
                    liste des riads et utilisateurs. Ne contient pas les données métier des tenants.
                  </>
                )}
                {createType === 'TENANT' && (
                  <>
                    <strong>Backup Tenant:</strong> Sauvegarde complète d'un riad: configurations, 
                    rôles, clients, réservations, paiements, dépenses, employés.
                  </>
                )}
                {createType === 'DATABASE' && (
                  <>
                    <strong>Backup Base de données:</strong> Copie complète de la base SQLite. 
                    Utile pour la récupération d'urgence.
                  </>
                )}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer le backup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce backup ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le fichier <strong>{deleteTarget?.filename}</strong> sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Confirmation */}
      <Dialog open={!!restoreTarget} onOpenChange={() => { setRestoreTarget(null); setRestoreConfirmation(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Restaurer un backup
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de restaurer le backup du tenant <strong>{restoreTarget?.riad_name}</strong>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>ATTENTION:</strong> Cette action va restaurer les configurations et rôles du tenant 
                à partir du backup. Les données métier (réservations, paiements) ne seront pas modifiées par sécurité.
              </p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                Pour confirmer, tapez <strong>RESTORE_CONFIRM</strong> ci-dessous:
              </p>
              <Input
                className="mt-2"
                placeholder="RESTORE_CONFIRM"
                value={restoreConfirmation}
                onChange={(e) => setRestoreConfirmation(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestoreTarget(null); setRestoreConfirmation(''); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleRestore}
              disabled={restoreConfirmation !== 'RESTORE_CONFIRM'}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restaurer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackupsPage;
